import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
} from "@tanstack/react-router";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";

import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuthBootstrap } from "@/hooks/useAuthBootstrap";
import { reportLovableError } from "@/lib/lovable-error-reporting";

import appCss from "../styles.css?url";

const NotFound = lazy(() => import("@/pages/NotFound"));

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "UTF-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { name: "theme-color", content: "#0b1d3a" },
      {
        name: "google-site-verification",
        content: "FvgdKgYh0Wkl8CaAvTZav899bv0jamIkpE0aI0J3Ea8",
      },
      { title: "LohaLink — India's Trusted B2B Scrap Metal Marketplace" },
      {
        name: "description",
        content:
          "LohaLink connects 3,400+ GST-verified Indian scrap yards with foundries. AI-benchmarked HMS, sponge iron & MS billet pricing with RBI-grade escrow.",
      },
      {
        name: "keywords",
        content:
          "scrap metal marketplace India, HMS 1 scrap, sponge iron suppliers, MS billets, GST verified scrap dealers, escrow scrap trade, B2B metal marketplace, foundry sourcing India, LohaLink",
      },
      { name: "author", content: "LohaLink" },
      {
        name: "robots",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      { name: "googlebot", content: "index, follow" },
      { name: "geo.region", content: "IN" },
      { name: "geo.placename", content: "India" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "LohaLink" },
      { property: "og:locale", content: "en_IN" },
      {
        property: "og:title",
        content: "LohaLink — India's Trusted B2B Scrap Metal Marketplace",
      },
      {
        property: "og:description",
        content:
          "3,400+ GST-verified scrap yards. AI-benchmarked pricing. RBI-grade escrow. Live truck tracking. India's trust-first marketplace for HMS, sponge iron and MS billets.",
      },
      { property: "og:url", content: "https://lohalink.in/" },
      { property: "og:image", content: "https://lohalink.in/og-image.jpg" },
      { property: "og:image:width", content: "1216" },
      { property: "og:image:height", content: "630" },
      {
        property: "og:image:alt",
        content:
          "LohaLink — India's Trusted Scrap Metal Marketplace, GST-Verified and Escrow-Protected",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@lohalink_in" },
      { name: "twitter:creator", content: "@lohalink_in" },
      {
        name: "twitter:title",
        content: "LohaLink — India's Trusted B2B Scrap Metal Marketplace",
      },
      {
        name: "twitter:description",
        content:
          "3,400+ GST-verified yards. AI-benchmarked HMS, sponge iron and MS billet pricing. Escrow-protected, live tracking.",
      },
      { name: "twitter:image", content: "https://lohalink.in/og-image.jpg" },
      {
        name: "twitter:image:alt",
        content: "LohaLink — India's Trusted Scrap Metal Marketplace",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      // No canonical here: TanStack concatenates `links` across matches, so a
      // root canonical would emit a second (wrong) canonical on every page.
      // Each leaf route sets its own canonical via buildHead().

      {
        rel: "preconnect",
        href: "https://ojszrkpbdlspkppxwdgw.supabase.co",
        crossOrigin: "anonymous",
      },
      { rel: "dns-prefetch", href: "https://ojszrkpbdlspkppxwdgw.supabase.co" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "LohaLink",
          alternateName: "LohaLink — The Scrap Metal Marketplace",
          url: "https://lohalink.in/",
          logo: "https://lohalink.in/logo.png",
          description:
            "India's trust-first B2B marketplace for bulk scrap metal — connecting GST-verified scrap yards with foundries via AI-benchmarked pricing and RBI-grade escrow.",
          foundingDate: "2024",
          areaServed: { "@type": "Country", name: "India" },
          contactPoint: [
            {
              "@type": "ContactPoint",
              telephone: "+91-93303-77207",
              contactType: "customer support",
              areaServed: "IN",
              availableLanguage: ["en", "hi", "bn"],
            },
            {
              "@type": "ContactPoint",
              telephone: "+91-79801-56276",
              contactType: "sales",
              areaServed: "IN",
              availableLanguage: ["en", "hi", "bn"],
            },
          ],
          sameAs: [
            "https://www.facebook.com/profile.php?id=61563635854204",
            "https://www.instagram.com/iron_scrap.in",
            "https://www.linkedin.com/company/lohalink-in",
            "https://x.com/lohalink_in",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "LohaLink",
          url: "https://lohalink.in/",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://lohalink.in/buyer?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en-IN" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

const RouteFallback = () => (
  <div className="container mx-auto space-y-4 p-6 animate-pulse">
    <div className="h-8 w-1/3 rounded-md bg-muted" />
    <div className="h-4 w-2/3 rounded-md bg-muted" />
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <div className="h-40 rounded-lg bg-muted" />
      <div className="h-40 rounded-lg bg-muted" />
      <div className="h-40 rounded-lg bg-muted" />
    </div>
  </div>
);

function AuthBootstrap() {
  useAuthBootstrap();

  // After initial render, warm up the most-used route chunks during idle time
  // so subsequent navigations feel instant. Pure prefetch — failures are silent.
  // ported from App.tsx
  if (typeof window !== "undefined") {
    const w = window as Window & { __ll_prefetched?: boolean };
    if (!w.__ll_prefetched) {
      w.__ll_prefetched = true;
      const prefetch = () => {
        void import("@/pages/Auth");
        void import("@/pages/SellerDashboard");
        void import("@/pages/BuyerDashboard");
      };
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => void })
        .requestIdleCallback;
      if (ric) ric(prefetch);
      else setTimeout(prefetch, 1500);
    }
  }

  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <AuthBootstrap />
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

function NotFoundComponent() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <NotFound />
    </Suspense>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md space-y-4 text-center">
        <h1 className="text-xl font-semibold text-foreground">This page didn't load</h1>
        <p className="text-sm text-muted-foreground">
          Something went wrong on our end. You can try again or head back home.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
            onClick={() => {
              void router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a
            className="rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground"
            href="/"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
